import React, { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';

interface LobbyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPlayVsComputer: (isPracticeMode: boolean) => void;
  onCreateLobby: (lobbyId: string, isPracticeMode: boolean) => void;
  onJoinLobby: (isPracticeMode: boolean) => void;
  onCloseLobby: () => void;
  myLobbyId: string | null;
  dailyLimit: {
    limit_reached: boolean;
    attempts_remaining: number;
  };
  isPracticeMode?: boolean;
}

interface Lobby {
  id: string;
  wallet_address: string;
  discord_username?: string | null;
  created_at: string;
  player_count: number;
}

export const LobbyModal: React.FC<LobbyModalProps> = ({
  isOpen,
  onClose,
  onPlayVsComputer,
  onCreateLobby,
  onJoinLobby,
  onCloseLobby,
  myLobbyId,
  dailyLimit,
  isPracticeMode = false
}) => {
  const [lobbies, setLobbies] = useState<Lobby[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreatingLobby, setIsCreatingLobby] = useState(false);
  const [userLobby, setUserLobby] = useState<Lobby | null>(null);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [shouldRender, setShouldRender] = useState<boolean>(false);
  const [localPracticeMode, setLocalPracticeMode] = useState<boolean>(false);

  // Блокировка прокрутки страницы при открытом модальном окне
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Очистка при размонтировании компонента
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const { publicKey } = useWallet();
  const walletAddress = publicKey?.toString() || null;
  
  // Загрузка списка лобби
  const fetchLobbies = async () => {
    if (!walletAddress) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/lobbies');
      
      if (!response.ok) {
        throw new Error('Failed to fetch lobbies');
      }
      
      const data = await response.json();
      setLobbies(data.lobbies || []);
      
      // Найти лобби текущего пользователя
      const myLobby = data.lobbies?.find((lobby: Lobby) => 
        lobby.wallet_address === walletAddress
      );
      setUserLobby(myLobby || null);
      
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to load lobbies. Please try again.';

      // Специальная обработка ошибки "Lobby not found"
      if (errorMessage.includes('Lobby not found')) {
        // Очищаем состояние лобби при ошибке "Lobby not found"
        setUserLobby(null);
        // Уведомляем родительский компонент о выходе из лобби
        onCloseLobby();
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Создание лобби
  const handleCreateLobby = async () => {
    if (!walletAddress) {
      setError('Please connect your wallet first');
      return;
    }

    if (!isPracticeMode && dailyLimit.limit_reached) {
      setError('Daily limit reached! You can play up to 5 rating games per day.');
      return;
    }
    
    if (userLobby) {
      setError('You already have an active lobby');
      return;
    }
    
    setIsCreatingLobby(true);
    setError(null);
    
    try {
      const response = await fetch('/api/lobbies/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          wallet_address: walletAddress,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create lobby');
      }
      
      const data = await response.json();
      
      // Вызываем callback для уведомления родительского компонента
      onCreateLobby(data.id.toString(), isPracticeMode);
      
      // Обновить список лобби
      await fetchLobbies();
      
    } catch (err: any) {
      setError(err.message || 'Failed to create lobby. Please try again.');
    } finally {
      setIsCreatingLobby(false);
    }
  };
  
  // Удаление лобби
  const handleCloseLobby = async () => {
    const lobbyIdToClose = userLobby?.id || myLobbyId;
    if (!lobbyIdToClose) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/lobbies/${lobbyIdToClose}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to close lobby');
      }
      
      setUserLobby(null);
      onCloseLobby(); // Уведомляем родительский компонент
      await fetchLobbies();
      
    } catch (err) {
      setError('Failed to close lobby. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Подключение к лобби
  const handleJoinLobby = async (lobbyId: string) => {
    if (!walletAddress) {
      setError('Please connect your wallet first');
      return;
    }

    if (!isPracticeMode && dailyLimit.limit_reached) {
      setError('Daily limit reached! You can play up to 5 rating games per day.');
      return;
    }
    
    setError(null);
    
    try {
      const response = await fetch(`/api/lobbies/${lobbyId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          wallet_address: walletAddress,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to join lobby');
      }
      
      // Уведомляем родительский компонент о подключении
      onJoinLobby(isPracticeMode);
      onClose();
      
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to join lobby. Please try again.';

      // Специальная обработка ошибки "Lobby not found"
      if (errorMessage.includes('Lobby not found')) {
        // Очищаем состояние лобби при ошибке "Lobby not found"
        setUserLobby(null);
        // Уведомляем родительский компонент о выходе из лобби
        onCloseLobby();
      }

      setError(errorMessage);
    }
  };
  
  // Загрузка лобби при открытии модального окна
  useEffect(() => {
    if (isOpen && walletAddress) {
      fetchLobbies();
    }
  }, [isOpen, walletAddress]);

  // Управление анимациями открытия/закрытия
  useEffect(() => {
    if (isOpen) {
      setLocalPracticeMode(isPracticeMode); // Сохраняем локальное состояние режима
      setShouldRender(true);
      // Small delay to ensure DOM is ready before animation
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isPracticeMode]);
  
  if (!shouldRender) return null;

  return (
    <div className="fixed inset-0 z-50">
      <style>{`
        .modal-content {
          opacity: 0;
          transform: scale(0.5);
          transition: all 0.15s ease-out;
        }

        .modal-content.entering {
          opacity: 1;
          transform: scale(1);
        }

        .modal-content.exiting {
          opacity: 0;
          transform: scale(0.5);
        }
      `}</style>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div
          className={`bg-black/80 rounded-xl shadow-xl border border-purple-800/50 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-border-glow modal-content ${
            isAnimating ? 'entering' : 'exiting'
          }`}
        >
          {/* Заголовок */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">
              {localPracticeMode ? 'Practice Lobbies' : 'Multiplayer Lobbies'}
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchLobbies}
                disabled={isLoading}
                className="text-purple-400 transition-colors p-2 disabled:opacity-50"
                title="Refresh lobbies"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${isLoading ? 'animate-spin' : ''} hover:text-white transition-colors`}>
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                </svg>
              </button>
              <button
                onClick={onClose}
                className="text-purple-400 transition-colors p-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="hover:text-white transition-colors">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>
          
          {/* Информация об ограничении попыток */}
          {/* {walletAddress && !isPracticeMode && (
            <div className="mb-4 p-3 bg-purple-900/30 rounded-lg border border-purple-700/50">
              <p className="text-purple-200 text-sm">
                Remaining games today: <span className="font-bold text-yellow-400">{dailyLimit.attempts_remaining}/5</span>
              </p>
            </div>
          )} */}
          
          {/* Ошибки */}
          {error && (
            <div className="mb-4 p-3 bg-red-900/30 rounded-lg border border-red-700/50">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}
          
          {/* Кнопка создания лобби */}
          {!userLobby && !myLobbyId && (
            <div className="mb-6">
              <button
                onClick={handleCreateLobby}
                disabled={isCreatingLobby || !walletAddress || (!isPracticeMode && dailyLimit.limit_reached)}
                className={`w-full py-3 px-4 border-2 border-purple-500/80 font-bold transition-all duration-100 ease-out ${
                  isCreatingLobby || !walletAddress || (!isPracticeMode && dailyLimit.limit_reached)
                    ? 'btn-textured-secondary text-purple-400 cursor-not-allowed opacity-50'
                    : 'btn-textured text-white'
                }`}
              >
                {isCreatingLobby ? 'Creating Lobby...' : 'Create Lobby'}
              </button>
            </div>
          )}
          
          {/* Список лобби */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-purple-300 mb-3">Available Lobbies</h3>
            
            {isLoading && !lobbies.length ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                <p className="mt-2 text-purple-300">Loading lobbies...</p>
              </div>
            ) : lobbies.length === 0 ? (
              <div className="text-center py-8 bg-purple-950/30 rounded-lg border border-purple-800/30">
                <p className="text-purple-300">No lobbies available. Create one to start playing!</p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <div className="space-y-3 pr-2">
                  {lobbies.map((lobby) => {
                  const isMyLobby = lobby.wallet_address === walletAddress;
                  const isFull = lobby.player_count >= 2;
                  
                  // Определяем отображаемое имя: Discord username или адрес кошелька
                  const displayName = lobby.discord_username 
                    ? lobby.discord_username 
                    : `${lobby.wallet_address.slice(0, 4)}...${lobby.wallet_address.slice(-4)}`;
                  
                  return (
                    <div
                      key={lobby.id}
                      className={`p-4 rounded-lg border transition-all duration-100 ease-out ${
                        isMyLobby
                          ? 'bg-purple-900/40 border-purple-600'
                          : 'bg-black/40 border-purple-800/30 hover:border-purple-700/50'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-white font-semibold">
                            {isMyLobby ? 'Your Lobby' : displayName}
                          </p>
                          <p className="text-purple-300 text-sm mt-1">
                            Players: {lobby.player_count}/2
                            {isFull && <span className="ml-2 text-yellow-400">(Full)</span>}
                          </p>
                        </div>
                        
                        <div>
                          {isMyLobby ? (
                            <button
                              onClick={handleCloseLobby}
                              disabled={isLoading}
                              className="py-2 px-4 btn-textured-danger text-white border-2 border-red-500/80 font-semibold transition-all duration-100 ease-out disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Close Lobby
                            </button>
                          ) : (
                            <button
                              onClick={() => handleJoinLobby(lobby.id)}
                              disabled={isFull || (!isPracticeMode && dailyLimit.limit_reached) || !walletAddress || myLobbyId !== null}
                              className={`py-2 px-4 border-2 border-green-500/80 font-semibold transition-all duration-100 ease-out ${
                                isFull || (!isPracticeMode && dailyLimit.limit_reached) || !walletAddress || myLobbyId !== null
                                  ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                                  : 'btn-textured-success text-white'
                              }`}
                              title={myLobbyId ? 'Close your lobby first to join another' : ''}
                            >
                              {isFull ? 'Full' : myLobbyId ? 'In Lobby' : 'Join'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
            )}
          </div>
          
          {/* Разделитель */}
          <div className="border-t border-purple-700/50 my-6"></div>
          
          {/* Игра против компьютера */}
          <div className="text-center">
            <p className="text-purple-300 mb-3">
              {localPracticeMode ? 'Practice your skills!' : "Don't want to wait for an opponent?"}
            </p>
            <button
              onClick={() => onPlayVsComputer(isPracticeMode)}
              disabled={(!isPracticeMode && dailyLimit.limit_reached) || !walletAddress || myLobbyId !== null}
              className={`py-3 px-6 border-2 border-purple-500/80 font-bold transition-all duration-100 ease-out ${
                (!isPracticeMode && dailyLimit.limit_reached) || !walletAddress || myLobbyId !== null
                  ? 'btn-textured-secondary text-purple-400 cursor-not-allowed opacity-50'
                  : 'btn-textured text-white'
              }`}
              title={myLobbyId ? `Close your lobby first to play vs ${localPracticeMode ? 'practice' : 'computer'}` : ''}
            >
              {localPracticeMode ? 'Practice vs Computer' : 'Play vs Computer'}
            </button>
          </div>
          
          {!walletAddress && (
            <p className="mt-4 text-yellow-500 text-sm text-center">
              Please connect your wallet to create or join lobbies
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

